if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer) return;

        // No 500 ms debounce: start as soon as the slider selects a slide.
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onVisibilityChange = this.handleVisibilityChange.bind(this);
        this.onGalleryTouch = this.handleGalleryTouch.bind(this);
        this.onGalleryTouchEnd = this.handleGalleryTouchEnd.bind(this);
      }

      connectedCallback() {
        if (!this.elements.viewer) return;
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.galleryVisible = true;
        if ('IntersectionObserver' in window) {
          this.visibilityObserver = new IntersectionObserver((entries) => {
            const wasVisible = this.galleryVisible;
            this.galleryVisible = entries[0].isIntersecting;
            if (this.mql.matches) return;
            if (!this.galleryVisible) {
              this.querySelectorAll('video').forEach((video) => {
                clearTimeout(video.metiniWarmTimer);
                video.pause();
              });
            } else if (!wasVisible && this.activeItem) {
              this.playActiveMedia(this.activeItem);
            }
          }, { threshold: 0 });
          this.visibilityObserver.observe(this.elements.viewer);
        }
        this.elements.viewer.addEventListener('touchstart', this.onGalleryTouch, { passive: true });
        this.elements.viewer.addEventListener('touchend', this.onGalleryTouchEnd, { passive: true });
        this.startFrame = requestAnimationFrame(() => {
          if (!this.isConnected) return;
          const active = this.elements.viewer.querySelector('.product__media-item.is-active') ||
            this.elements.viewer.querySelector('.product__media-item');
          if (!active) return;
          this.activeItem = active;
          const video = this.prepareVideo(active);
          if (video) this.playActiveMedia(active);

          // Let the main product image finish first, not all apps/window.load.
          const image = active.querySelector('.product__modal-opener--image img');
          this.onHeroReady = () => {
            this.warmupEnabled = true;
            this.warmUpcomingVideo();
          };
          this.heroImage = image;
          if (image && !image.complete) {
            image.addEventListener('load', this.onHeroReady, { once: true });
            image.addEventListener('error', this.onHeroReady, { once: true });
          } else {
            this.onHeroReady();
          }
        });
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.startFrame);
        this.visibilityObserver?.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.elements.viewer?.removeEventListener('touchstart', this.onGalleryTouch);
        this.elements.viewer?.removeEventListener('touchend', this.onGalleryTouchEnd);
        if (this.heroImage && this.onHeroReady) {
          this.heroImage.removeEventListener('load', this.onHeroReady);
          this.heroImage.removeEventListener('error', this.onHeroReady);
        }
        this.querySelectorAll('video').forEach((video) => {
          clearTimeout(video.metiniWarmTimer);
          video.pause();
        });
      }

      onSlideChanged(event) {
        const currentMedia = event.detail.currentElement;
        if (!currentMedia) return;

        // Mobile swipe does not call setActiveMedia(), so explicitly mark
        // the newly visible slide as active and start its media.
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        currentMedia.classList.add('is-active');

        this.playActiveMedia(currentMedia);

        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${currentMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      // METINI fast video startup. Only the next Shopify-hosted video is
      // prepared, using the SAME element that will later be shown in the slide.
      // No fetch/blob copy, hidden iframe or background download of every video.
      prepareVideo(slide) {
        const deferred = slide?.querySelector('deferred-media');
        const templateVideo = deferred?.querySelector('template')?.content.querySelector('video');
        if (!deferred || !templateVideo) return null; // Leave YouTube / 3D alone.
        let video = deferred.querySelector('video');
        if (video?.metiniPrepared) return video;
        const isNew = !video;
        video = video || templateVideo.cloneNode(true);
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = true;
        video.autoplay = false; // The gallery, not an offscreen autoplay, owns play().
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('loop', '');
        video.removeAttribute('autoplay');
        video.removeAttribute('controls');
        video.setAttribute('loading', 'eager');
        video.preload = 'auto';

        // Liquid supplies a real Shopify MP4 rendition, never a guessed CDN URL.
        // Set it BEFORE insertion/load, so two versions are not downloaded.
        if (isNew && !this.mql.matches && deferred.dataset.metiniMobileSrc) {
          video.querySelectorAll('source').forEach((source) => source.remove());
          video.src = deferred.dataset.metiniMobileSrc;
        }
        video.metiniPrepared = true;
        const feedback = document.createElement('button');
        feedback.type = 'button';
        feedback.className = 'metini-video-feedback';
        feedback.hidden = true;
        feedback.addEventListener('click', () => {
          this.playActiveMedia(slide, true);
        });
        video.metiniFeedback = feedback;
        video.addEventListener('playing', () => {
          clearTimeout(video.metiniWarmTimer);
          video.metiniWarming = false;
          if (this.activeItem !== slide || document.hidden || !this.isConnected || (!this.mql.matches && !this.galleryVisible)) {
            // Warm-up: enough data to start has arrived. Don't run offscreen loops.
            video.pause();
            return;
          }
          this.setVideoState(video, 'playing');
          this.warmUpcomingVideo();
        });
        video.addEventListener('waiting', () => {
          if (this.activeItem === slide && !video.paused) this.setVideoState(video, 'loading');
        });
        video.addEventListener('error', () => {
          if (this.activeItem === slide) this.setVideoState(video, 'retry');
        });
        if (isNew) deferred.appendChild(video);
        deferred.appendChild(feedback);
        deferred.setAttribute('loaded', 'true');
        // Never call load() on each swipe: it discards useful buffered data.
        if (isNew) video.load();
        return video;
      }

      setVideoState(video, state) {
        const feedback = video.metiniFeedback;
        if (!feedback) return;
        const polish = document.documentElement.lang.toLowerCase().startsWith('pl');
        feedback.hidden = state === 'playing' || state === 'idle';
        feedback.disabled = state === 'loading';
        feedback.dataset.state = state;
        feedback.textContent = state === 'loading'
          ? (polish ? 'Ładowanie filmu…' : 'Loading video…')
          : (polish ? 'Odtwórz film' : 'Play video');
      }

      pauseOtherMedia(keepVideo) {
        // Unlike pauseAllMedia(), don't pause the very element we're starting.
        document.querySelectorAll('video').forEach((video) => {
          if (video === keepVideo) return;
          clearTimeout(video.metiniWarmTimer);
          if (video.metiniWarming && video.readyState < 2) video.metiniWarmAttempted = false;
          video.metiniWarming = false;
          video.pause();
        });
        document.querySelectorAll('.js-youtube').forEach((frame) => {
          frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        });
        document.querySelectorAll('.js-vimeo').forEach((frame) => {
          frame.contentWindow?.postMessage('{"method":"pause"}', '*');
        });
        document.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause());
      }

      playActiveMedia(activeItem, userInitiated = false) {
        if (!activeItem) return;
        const changed = this.activeItem !== activeItem;
        this.activeItem = activeItem;
        const deferred = activeItem.querySelector('.deferred-media');
        const video = this.prepareVideo(activeItem);
        if (!video) {
          if (changed) window.pauseAllMedia();
          if (deferred && (changed || userInitiated)) deferred.loadContent(false);
          this.warmUpcomingVideo();
          return;
        }
        this.pauseOtherMedia(video);
        clearTimeout(video.metiniWarmTimer);
        video.metiniWarming = false;
        if (document.hidden || (!this.mql.matches && !this.galleryVisible)) return;
        if (this.reduceMotion.matches && !userInitiated) {
          video.pause();
          video.controls = true;
          this.setVideoState(video, 'retry');
          return;
        }
        this.setVideoState(video, video.readyState >= 3 && !video.paused ? 'playing' : 'loading');
        // play() requests data straight away; don't wait for canplaythrough.
        const promise = video.play();
        if (promise?.catch) promise.catch((error) => {
          if (this.activeItem !== activeItem || document.hidden || !this.isConnected) return;
          if (error.name === 'AbortError') return; // A subsequent swipe paused it.
          this.setVideoState(video, 'retry'); // Respect browser autoplay restrictions.
        });
      }

      warmUpcomingVideo() {
        if (!this.isConnected || !this.warmupEnabled || !this.galleryVisible || document.hidden || this.mql.matches ||
            this.reduceMotion.matches || navigator.connection?.saveData) return;
        const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'))
          .filter((slide) => slide.clientWidth > 0);
        const index = slides.indexOf(this.activeItem);
        if (index < 0) return;
        const activeVideo = this.activeItem.querySelector('deferred-media video');
        // Don't compete for bandwidth until the active video has enough data.
        if (activeVideo && activeVideo.readyState < 3) return;
        const ordered = slides.slice(index + 1).concat(slides.slice(0, index));
        const next = ordered.find((slide) =>
          slide.querySelector('deferred-media template')?.content.querySelector('video')
        );
        if (!next) return;
        const video = this.prepareVideo(next);
        if (!video || video.metiniWarmAttempted || video.readyState >= 3) return;
        video.metiniWarmAttempted = true;
        video.metiniWarming = true;
        // preload is a hint and iOS can limit it. A short MUTED play request
        // prepares the decoder; the 'playing' event immediately pauses it.
        // A rejected request is respected, with preload left as a fallback.
        video.metiniWarmTimer = setTimeout(() => {
          video.metiniWarming = false;
          if (this.activeItem !== next) video.pause();
        }, 4000);
        const promise = video.play();
        if (promise?.catch) promise.catch(() => {
          clearTimeout(video.metiniWarmTimer);
          video.metiniWarming = false;
        });
      }

      handleGalleryTouch() {
        this.warmupEnabled = true;
        this.warmUpcomingVideo();
      }

      handleGalleryTouchEnd() {
        // A real touch also provides a retry opportunity in restrictive browsers.
        const video = this.activeItem?.querySelector('deferred-media video');
        if (video?.paused && !this.reduceMotion.matches) this.playActiveMedia(this.activeItem);
      }

      handleVisibilityChange() {
        if (document.hidden) {
          this.querySelectorAll('video').forEach((video) => {
            clearTimeout(video.metiniWarmTimer);
            video.pause();
          });
        } else if (this.activeItem) {
          this.playActiveMedia(this.activeItem);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
